/**
 * KanbanCore.ts - Framework-agnostic core logic for OpCentral-compliant KanbanView
 * 
 * CHANGE HISTORY:
 * - 251108_0842 [current] danielbeer: feat: Add filtering, templates, relationships, and bulk operations
 * - 251107_2245 danielbeer: feat: Create KanbanCore with full orchestrator integration
 */

import {
  KanbanConfig,
  KanbanColumn,
  KanbanFilter,
  KanbanCardTemplate,
  KanbanRelationship,
  createKanbanConfig,
  validateKanbanConfig,
  getFieldValue,
  setFieldValue,
  applyFilters,
  searchItems,
  getCardAge,
  getCardAgeIndicator,
} from './KanbanConfig';

/**
 * KanbanCore - Framework-agnostic core logic for Kanban board
 * 
 * Handles all business logic and state management without any framework dependencies.
 * Integrates with OpCentral orchestrators for CRUD, UI, persistence, and profile management.
 */
export class KanbanCore {
  config: KanbanConfig;
  actr: any;
  aggregatr: any;
  data: any[];
  columns: KanbanColumn[];
  subscribers: ((state: any) => void)[];
  
  // Selection state
  selectedCardIds: Set<string>;
  lastSelectedCardId: string | null;
  
  // Drag-and-drop state
  activeDragCardId: string | null;
  activeDragSourceColumn: string | null;
  
  // Filtering and search state
  activeFilters: KanbanFilter[];
  searchQuery: string;
  
  // Relationships
  relationships: KanbanRelationship[];
  
  constructor(config: Partial<KanbanConfig> = {}) {
    // Merge with defaults and validate
    this.config = createKanbanConfig(config);
    const validation = validateKanbanConfig(this.config);
    
    if (!validation.valid) {
      console.error('KanbanView configuration errors:', validation.errors);
      throw new Error('Invalid KanbanView configuration');
    }
    
    // Store orchestrator references
    this.actr = this.config.actr;
    this.aggregatr = this.config.aggregatr;
    
    // Initialize state
    this.data = [];
    this.columns = this.config.columns || [];
    this.subscribers = [];
    this.selectedCardIds = new Set();
    this.lastSelectedCardId = null;
    this.activeDragCardId = null;
    this.activeDragSourceColumn = null;
    this.activeFilters = [];
    this.searchQuery = '';
    this.relationships = this.config.relationships || [];
  }
  
  // ==========================================================================
  // DATA MANAGEMENT
  // ==========================================================================
  
  /**
   * Set data and persist to Aggregatr
   */
  async setData(data: any[]): Promise<void> {
    this.data = data;
    this.notify();
    
    // Persist to Aggregatr if available and has the method
    if (this.aggregatr && this.config.workspaceId && this.config.datasetId) {
      try {
        // Check if saveDataset method exists
        if (typeof this.aggregatr.saveDataset === 'function') {
          await this.aggregatr.saveDataset(
            this.config.workspaceId,
            this.config.datasetId,
            data
          );
        }
      } catch (error) {
        console.error('Aggregatr save failed:', error);
      }
    }
  }
  
  /**
   * Load data from Aggregatr
   */
  async loadData(): Promise<any[]> {
    if (this.aggregatr && this.config.workspaceId && this.config.datasetId) {
      try {
        // Check if loadDataset method exists
        if (typeof this.aggregatr.loadDataset === 'function') {
          const data = await this.aggregatr.loadDataset(
            this.config.workspaceId,
            this.config.datasetId
          );
          if (data) {
            await this.setData(data);
            return data;
          }
        }
      } catch (error) {
        console.error('Aggregatr load failed:', error);
      }
    }
    return [];
  }
  
  /**
   * Get current data
   */
  getData(): any[] {
    return this.data;
  }
  
  /**
   * Get card by ID
   */
  getCard(cardId: string): any {
    return this.data.find(card => getFieldValue(card, 'idField', this.config) === cardId);
  }
  
  /**
   * Get column by ID
   */
  getColumn(columnId: string): KanbanColumn | undefined {
    return this.columns.find(col => col.id === columnId);
  }
  
  /**
   * Get cards in a column
   * Items without a status default to 'active' (matching Producr behavior)
   */
  getCardsInColumn(columnId: string): any[] {
    return this.data.filter(card => {
      const status = getFieldValue(card, 'columnField', this.config) || 'active';
      return status === columnId;
    });
  }
  
  // ==========================================================================
  // CARD CRUD OPERATIONS (THROUGH ACTR)
  // ==========================================================================
  
  /**
   * Add card to column
   * Routes through Actr for logging, permissions, audit trail
   */
  async addCard(columnId: string, cardData: any): Promise<string> {
    // Route through Actr if available
    if (this.actr) {
      try {
        const result = await this.actr.execute('kanban:add-card', {
          columnId,
          cardData,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return result.id;
      } catch (error) {
        console.error('Actr add-card failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // Fallback to callback
    if (this.config.onCardAdd) {
      this.config.onCardAdd(columnId, cardData);
      this.notify();
      return cardData[this.config.fieldMappings?.idField || 'id'];
    }
    
    // Internal implementation
    const newCard = {
      ...cardData,
      [this.config.fieldMappings?.idField || 'id']: crypto.randomUUID(),
      [this.config.fieldMappings?.columnField || 'status']: columnId,
    };
    this.data.push(newCard);
    await this.setData(this.data);
    return newCard[this.config.fieldMappings?.idField || 'id'];
  }
  
  /**
   * Update card
   * Routes through Actr for logging, permissions, audit trail
   */
  async updateCard(cardId: string, updates: any): Promise<boolean> {
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('kanban:update-card', {
          cardId,
          updates,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr update-card failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // Fallback to callback
    if (this.config.onCardUpdate) {
      this.config.onCardUpdate(cardId, updates);
      this.notify();
      return true;
    }
    
    // Internal implementation
    const card = this.getCard(cardId);
    if (card) {
      Object.assign(card, updates);
      await this.setData(this.data);
      return true;
    }
    return false;
  }
  
  /**
   * Delete card
   * Routes through Actr for logging, permissions, audit trail
   */
  async deleteCard(cardId: string): Promise<boolean> {
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('kanban:delete-card', {
          cardId,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr delete-card failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // Fallback to callback
    if (this.config.onCardDelete) {
      this.config.onCardDelete(cardId);
      this.notify();
      return true;
    }
    
    // Internal implementation
    const index = this.data.findIndex(card => 
      getFieldValue(card, 'idField', this.config) === cardId
    );
    if (index !== -1) {
      this.data.splice(index, 1);
      await this.setData(this.data);
      return true;
    }
    return false;
  }
  
  /**
   * Move card to different column or reorder within column
   * Routes through Actr for logging, permissions, audit trail
   */
  async moveCard(cardId: string, toColumn: string, position?: number): Promise<boolean> {
    const card = this.getCard(cardId);
    if (!card) return false;
    
    const fromColumn = getFieldValue(card, 'columnField', this.config);
    
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('kanban:move-card', {
          cardId,
          fromColumn,
          toColumn,
          position,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr move-card failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // Fallback to callback
    if (this.config.onCardMove) {
      this.config.onCardMove(cardId, fromColumn, toColumn, position || 0);
      this.notify();
      return true;
    }
    
    // Internal implementation
    const updates: any = {};
    
    // Update column if moving to different column
    if (fromColumn !== toColumn) {
      updates[this.config.fieldMappings?.columnField || 'status'] = toColumn;
    }
    
    // Update order if position specified
    if (position !== undefined && this.config.fieldMappings?.orderField) {
      updates[this.config.fieldMappings.orderField] = position;
    }
    
    Object.assign(card, updates);
    await this.setData(this.data);
    return true;
  }
  
  // ==========================================================================
  // COLUMN CRUD OPERATIONS (THROUGH ACTR)
  // ==========================================================================
  
  /**
   * Add column
   * Routes through Actr for logging, permissions, audit trail
   */
  async addColumn(columnData: Partial<KanbanColumn>): Promise<string> {
    // Route through Actr if available
    if (this.actr) {
      try {
        const result = await this.actr.execute('kanban:add-column', {
          columnData,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return result.id;
      } catch (error) {
        console.error('Actr add-column failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // Fallback to callback
    if (this.config.onColumnAdd) {
      this.config.onColumnAdd(columnData);
      this.notify();
      return columnData.id || crypto.randomUUID();
    }
    
    // Internal implementation
    const newColumn: KanbanColumn = {
      id: columnData.id || crypto.randomUUID(),
      title: columnData.title || 'New Column',
      color: columnData.color,
      limit: columnData.limit,
      order: columnData.order ?? this.columns.length,
      collapsed: columnData.collapsed,
    };
    this.columns.push(newColumn);
    this.notify();
    return newColumn.id;
  }
  
  /**
   * Update column
   * Routes through Actr for logging, permissions, audit trail
   */
  async updateColumn(columnId: string, updates: Partial<KanbanColumn>): Promise<boolean> {
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('kanban:update-column', {
          columnId,
          updates,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr update-column failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // Fallback to callback
    if (this.config.onColumnUpdate) {
      this.config.onColumnUpdate(columnId, updates);
      this.notify();
      return true;
    }
    
    // Internal implementation
    const column = this.getColumn(columnId);
    if (column) {
      Object.assign(column, updates);
      this.notify();
      return true;
    }
    return false;
  }
  
  /**
   * Delete column
   * Routes through Actr for logging, permissions, audit trail
   */
  async deleteColumn(columnId: string): Promise<boolean> {
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('kanban:delete-column', {
          columnId,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr delete-column failed:', error);
        // Fall through to callback or internal
      }
    }
    
    // Fallback to callback
    if (this.config.onColumnDelete) {
      this.config.onColumnDelete(columnId);
      this.notify();
      return true;
    }
    
    // Internal implementation
    const index = this.columns.findIndex(col => col.id === columnId);
    if (index !== -1) {
      this.columns.splice(index, 1);
      this.notify();
      return true;
    }
    return false;
  }
  
  /**
   * Reorder columns
   * Routes through Actr for logging, permissions, audit trail
   */
  async reorderColumns(columnOrder: string[]): Promise<boolean> {
    // Route through Actr if available
    if (this.actr) {
      try {
        await this.actr.execute('kanban:reorder-columns', {
          columnOrder,
          workspaceId: this.config.workspaceId,
          datasetId: this.config.datasetId,
        });
        this.notify();
        return true;
      } catch (error) {
        console.error('Actr reorder-columns failed:', error);
        // Fall through to internal
      }
    }
    
    // Internal implementation
    const reordered: KanbanColumn[] = [];
    columnOrder.forEach((columnId, index) => {
      const column = this.getColumn(columnId);
      if (column) {
        column.order = index;
        reordered.push(column);
      }
    });
    
    this.columns = reordered;
    this.notify();
    return true;
  }
  
  // ==========================================================================
  // SELECTION MANAGEMENT
  // ==========================================================================
  
  /**
   * Select card(s)
   */
  selectCard(cardId: string, options: { multi?: boolean; toggle?: boolean } = {}): void {
    if (options.toggle && this.selectedCardIds.has(cardId)) {
      this.selectedCardIds.delete(cardId);
    } else if (options.multi) {
      this.selectedCardIds.add(cardId);
    } else {
      this.selectedCardIds.clear();
      this.selectedCardIds.add(cardId);
    }
    
    this.lastSelectedCardId = cardId;
    this.notify();
    
    if (this.config.onCardSelect) {
      this.config.onCardSelect(Array.from(this.selectedCardIds));
    }
  }
  
  /**
   * Clear selection
   */
  clearSelection(): void {
    this.selectedCardIds.clear();
    this.lastSelectedCardId = null;
    this.notify();
    
    if (this.config.onCardSelect) {
      this.config.onCardSelect([]);
    }
  }
  
  /**
   * Get current selection
   */
  getSelection(): { cardIds: string[]; lastCardId: string | null; count: number } {
    return {
      cardIds: Array.from(this.selectedCardIds),
      lastCardId: this.lastSelectedCardId,
      count: this.selectedCardIds.size,
    };
  }
  
  // ==========================================================================
  // DRAG-AND-DROP STATE MANAGEMENT
  // ==========================================================================
  
  /**
   * Start drag operation
   */
  startDrag(cardId: string, sourceColumn: string): void {
    this.activeDragCardId = cardId;
    this.activeDragSourceColumn = sourceColumn;
    this.notify();
  }
  
  /**
   * End drag operation
   */
  endDrag(): void {
    this.activeDragCardId = null;
    this.activeDragSourceColumn = null;
    this.notify();
  }
  
  /**
   * Get active drag info
   */
  getActiveDrag(): { cardId: string | null; sourceColumn: string | null } {
    return {
      cardId: this.activeDragCardId,
      sourceColumn: this.activeDragSourceColumn,
    };
  }
  
  // ==========================================================================
  // STATE MANAGEMENT
  // ==========================================================================
  
  /**
   * Get current state
   */
  getState(): any {
    return {
      data: this.data,
      columns: this.columns,
      selection: this.getSelection(),
      activeDrag: this.getActiveDrag(),
      config: this.config,
    };
  }
  
  /**
   * Subscribe to state changes
   */
  subscribe(callback: (state: any) => void): () => void {
    this.subscribers.push(callback);
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }
  
  /**
   * Notify subscribers of state change
   */
  notify(): void {
    const state = this.getState();
    this.subscribers.forEach(callback => callback(state));
  }
  
  // ==========================================================================
  // FILTERING & SEARCH
  // ==========================================================================
  
  /**
   * Set active filters
   */
  setFilters(filters: KanbanFilter[]): void {
    this.activeFilters = filters;
    this.notify();
  }
  
  /**
   * Add filter
   */
  addFilter(filter: KanbanFilter): void {
    if (!this.activeFilters.find(f => f.id === filter.id)) {
      this.activeFilters.push(filter);
      this.notify();
    }
  }
  
  /**
   * Remove filter
   */
  removeFilter(filterId: string): void {
    this.activeFilters = this.activeFilters.filter(f => f.id !== filterId);
    this.notify();
  }
  
  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.activeFilters = [];
    this.searchQuery = '';
    this.notify();
  }
  
  /**
   * Set search query
   */
  setSearchQuery(query: string): void {
    this.searchQuery = query;
    this.notify();
  }
  
  /**
   * Get filtered data
   */
  getFilteredData(): any[] {
    let filtered = this.data;
    
    // Apply filters
    if (this.activeFilters.length > 0) {
      filtered = applyFilters(filtered, this.activeFilters);
    }
    
    // Apply search
    if (this.searchQuery && this.config.searchFields) {
      filtered = searchItems(filtered, this.searchQuery, this.config.searchFields);
    }
    
    return filtered;
  }
  
  // ==========================================================================
  // CARD TEMPLATES
  // ==========================================================================
  
  /**
   * Create card from template
   */
  async createCardFromTemplate(template: KanbanCardTemplate, columnId: string, additionalData: any = {}): Promise<string> {
    const cardData = {
      ...template.defaultData,
      ...additionalData,
    };
    
    // Validate required fields
    if (template.requiredFields) {
      const missing = template.requiredFields.filter(field => !cardData[field]);
      if (missing.length > 0) {
        throw new Error(`Missing required fields: ${missing.join(', ')}`);
      }
    }
    
    return await this.addCard(columnId, cardData);
  }
  
  // ==========================================================================
  // RELATIONSHIPS
  // ==========================================================================
  
  /**
   * Add relationship between cards
   */
  async addRelationship(relationship: KanbanRelationship): Promise<void> {
    // Validate cards exist
    const fromCard = this.getCard(relationship.fromCardId);
    const toCard = this.getCard(relationship.toCardId);
    
    if (!fromCard || !toCard) {
      throw new Error('Cannot create relationship: one or both cards not found');
    }
    
    // Check for duplicate
    const exists = this.relationships.some(
      r => r.fromCardId === relationship.fromCardId &&
           r.toCardId === relationship.toCardId &&
           r.type === relationship.type
    );
    
    if (exists) {
      throw new Error('Relationship already exists');
    }
    
    this.relationships.push(relationship);
    this.notify();
    
    // Persist if Aggregatr available
    if (this.aggregatr && this.config.workspaceId) {
      try {
        await this.aggregatr.saveRelationships(
          this.config.workspaceId,
          this.config.datasetId,
          this.relationships
        );
      } catch (error) {
        console.error('Failed to persist relationships:', error);
      }
    }
  }
  
  /**
   * Remove relationship
   */
  async removeRelationship(fromCardId: string, toCardId: string, type: string): Promise<void> {
    this.relationships = this.relationships.filter(
      r => !(r.fromCardId === fromCardId && r.toCardId === toCardId && r.type === type)
    );
    this.notify();
    
    // Persist if Aggregatr available
    if (this.aggregatr && this.config.workspaceId) {
      try {
        await this.aggregatr.saveRelationships(
          this.config.workspaceId,
          this.config.datasetId,
          this.relationships
        );
      } catch (error) {
        console.error('Failed to persist relationships:', error);
      }
    }
  }
  
  /**
   * Get relationships for a card
   */
  getCardRelationships(cardId: string): KanbanRelationship[] {
    return this.relationships.filter(
      r => r.fromCardId === cardId || r.toCardId === cardId
    );
  }
  
  /**
   * Get related cards
   */
  getRelatedCards(cardId: string): { card: any; relationship: KanbanRelationship }[] {
    const related: { card: any; relationship: KanbanRelationship }[] = [];
    
    this.relationships.forEach(rel => {
      if (rel.fromCardId === cardId) {
        const card = this.getCard(rel.toCardId);
        if (card) {
          related.push({ card, relationship: rel });
        }
      } else if (rel.toCardId === cardId) {
        const card = this.getCard(rel.fromCardId);
        if (card) {
          related.push({ card, relationship: rel });
        }
      }
    });
    
    return related;
  }
  
  // ==========================================================================
  // BULK OPERATIONS
  // ==========================================================================
  
  /**
   * Move multiple cards to a column
   */
  async bulkMoveCards(cardIds: string[], targetColumnId: string): Promise<void> {
    for (const cardId of cardIds) {
      const card = this.getCard(cardId);
      if (card) {
        await this.moveCard(cardId, targetColumnId);
      }
    }
  }
  
  /**
   * Delete multiple cards
   */
  async bulkDeleteCards(cardIds: string[]): Promise<void> {
    for (const cardId of cardIds) {
      await this.deleteCard(cardId);
    }
    this.clearSelection();
  }
  
  /**
   * Update multiple cards
   */
  async bulkUpdateCards(cardIds: string[], updates: any): Promise<void> {
    for (const cardId of cardIds) {
      await this.updateCard(cardId, updates);
    }
  }
  
  /**
   * Duplicate card
   */
  async duplicateCard(cardId: string): Promise<string> {
    const card = this.getCard(cardId);
    if (!card) {
      throw new Error('Card not found');
    }
    
    const columnId = getFieldValue(card, 'columnField', this.config);
    const duplicate = { ...card };
    
    // Remove ID so a new one is generated
    const idField = this.config.fieldMappings?.idField || 'id';
    delete duplicate[idField];
    
    // Update title to indicate duplicate
    const titleField = this.config.fieldMappings?.titleField || 'title';
    if (duplicate[titleField]) {
      duplicate[titleField] = `${duplicate[titleField]} (Copy)`;
    }
    
    return await this.addCard(columnId, duplicate);
  }
  
  // ==========================================================================
  // CARD AGE & WIP LIMITS
  // ==========================================================================
  
  /**
   * Get card age information
   */
  getCardAgeInfo(cardId: string): { age: number; indicator: 'normal' | 'warning' | 'danger' } {
    const card = this.getCard(cardId);
    if (!card) {
      return { age: 0, indicator: 'normal' };
    }
    
    return {
      age: getCardAge(card, this.config),
      indicator: getCardAgeIndicator(card, this.config),
    };
  }
  
  /**
   * Check if column is at WIP limit
   */
  isColumnAtLimit(columnId: string): boolean {
    const column = this.getColumn(columnId);
    if (!column || !column.limit) return false;
    
    const cardsInColumn = this.getCardsInColumn(columnId);
    return cardsInColumn.length >= column.limit;
  }
  
  /**
   * Get column WIP status
   */
  getColumnWipStatus(columnId: string): { count: number; limit: number | undefined; atLimit: boolean; overLimit: boolean } {
    const column = this.getColumn(columnId);
    const count = this.getCardsInColumn(columnId).length;
    const limit = column?.limit;
    
    return {
      count,
      limit,
      atLimit: limit ? count >= limit : false,
      overLimit: limit ? count > limit : false,
    };
  }
}
